import React from 'react'
import { Box, Typography, Chip, Paper } from '@mui/material'
import ArticleIcon from '@mui/icons-material/Article'
import GavelIcon from '@mui/icons-material/Gavel'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import PublicIcon from '@mui/icons-material/Public'
import DescriptionIcon from '@mui/icons-material/Description'
import { openDocumentInTab } from '../services/tabManager'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
const RelatedDocuments = ({ relatedDocs, showSnackbar }) => {
  // Don't render if no related docs
  if (!relatedDocs || relatedDocs.length === 0) {
    return null
  }

  // Decision type colors
  const decisionTypeColors = {
    'Cabinet Decision': {
      main: '#4e342e',
      light: '#efebe9',
    },
    'Ministerial Decision': {
      main: '#1976d2',
      light: '#e3f2fd',
    },
    'Federal Tax Authority Decision': {
      main: '#2e7d32',
      light: '#e8f5e9',
    },
    'Administrative Decision': {
      main: '#4e342e', // Brown
      light: '#efebe9',
    },
    'ZATCA Decision': {
      main: '#d81b60',
      light: '#fce4ec',
    },
    Bylaws: {
      main: '#0097a7',
      light: '#e0f7fa',
    },
    'Executive Rules and Instructions': {
      main: '#673ab7',
      light: '#ede7f6',
    },
    'Executive Regulations': {
      main: '#673ab7',
      light: '#ede7f6',
    },
    'Implementing Regulations': {
      main: '#673ab7',
      light: '#ede7f6',
    },
    'Executive Bylaws': {
      main: '#673ab7',
      light: '#ede7f6',
    },
    'Judicial Decision': {
      main: '#673ab7',
      light: '#ede7f6',
    },
  }

  // Guide type colors
  const guideTypeColors = {
    'Public Clarification': {
      main: '#f44336',
      light: '#ffebee',
    },
    'Federal Tax Authority Guide': {
      main: '#3f51b5',
      light: '#e8eaf6',
    },
    'Federal Tax Authority Service Guide': {
      main: '#d81b60',
      light: '#fce4ec',
    },
    'Zakat, Tax and Customs Authority': {
      main: '#2e7d32',
      light: '#e8f5e9',
    },
    'Foreign Account Tax Compliance Act Guide': {
      main: '#673ab7',
      light: '#ede7f6',
    },
    'User Manual': {
      main: '#ff9800',
      light: '#fff3e0',
    },
    'VAT Taxpayer Guide': {
      main: '#0097a7',
      light: '#e0f7fa',
    },
    'Tax Bulletin and Circular': {
      main: '#d81b60',
      light: '#fce4ec',
    },
    Circular: {
      main: '#1976d2',
      light: '#e3f2fd',
    },
    'Oman Tax Authority': {
      main: '#37474f',
      light: '#eceff1',
    },
    'Qatar Tax Authority': {
      main: '#37474f',
      light: '#eceff1',
    },
    'National Bureau for Revenue': {
      main: '#37474f',
      light: '#eceff1',
    },
    'Foreign Account Tax Compliance Act FAQ and Flowchart': {
      main: '#3f51b5',
      light: '#e8eaf6',
    }
  }

  // Default colors for each main category
  const defaultColors = {
    article: {
      main: '#5948CA',
      light: '#e8e6f5',
    },
    decision: {
      main: '#2e7d32',
      light: '#e8f5e9',
    },
    guide: {
      main: '#3f51b5',
      light: '#e8eaf6',
    },
    treaty: {
      main: '#0097a7',
      light: '#e0f7fa',
    },
  }

  /**
   * Get document information including type, path, icon, and colors
   * @param {string} slug - Document slug
   * @returns {object} Document info object
   */
  const getDocumentInfo = (slug) => {
    // Articles
    if (slug.includes('-article-')) {
      return {
        typeLabel: 'Law-Article',
        icon: <ArticleIcon fontSize='small' />,
        path: '/articles',
        category: 'article',
        colors: defaultColors.article,
      }
    }

    // Decisions - detect specific type from slug pattern
    if (slug.match(/-(cd|md|fta|ad|zd|td|bl|er|ers|ir|eb|jd)-/i)) {
      const match = slug.match(/-(cd|md|fta|ad|zd|td|bl|er|ers|ir|eb|jd)-/i)
      const decisionCode = match[1].toLowerCase()

      let specificType = 'Decision'
      let colors = defaultColors.decision

      // Map decision codes to types
      const decisionTypeMap = {
        cd: 'Cabinet Decision',
        md: 'Ministerial Decision',
        fta: 'Federal Tax Authority Decision',
        ad: 'Administrative Decision',
        zd: 'ZATCA Decision',
        bl: 'Bylaws',
        er: 'Executive Rules and Instructions',
        ers: 'Executive Regulations',
        ir: 'Implementing Regulations',
        eb: 'Executive Bylaws',
        jd: 'Judicial Decision',
      }

      specificType = decisionTypeMap[decisionCode] || 'Decision'
      colors = decisionTypeColors[specificType] || defaultColors.decision

      return {
        typeLabel: 'Decision',
        icon: <GavelIcon fontSize='small' />,
        path: '/decisions',
        category: 'decision',
        colors,
        specificType,
      }
    }

    // Guidelines/Supplementary Publications
    if (slug.match(/-(guide|pc|circular|tbc)-/i)) {
      let specificType = 'Federal Tax Authority Guide'
      let colors = defaultColors.guide

      // Try to detect guide type from slug
      if (slug.includes('-pc-')) {
        specificType = 'Public Clarification'
        colors = guideTypeColors['Public Clarification'] || defaultColors.guide
      } else if (slug.includes('-circular-')) {
        specificType = 'Circular'
        colors = guideTypeColors['Circular'] || defaultColors.guide
      } else if (slug.includes('-tbc-')) {
        specificType = 'Tax Bulletin and Circular'
        colors =
          guideTypeColors['Tax Bulletin and Circular'] || defaultColors.guide
      }

      return {
        typeLabel: 'Supplementary Publications',
        icon: <MenuBookIcon fontSize='small' />,
        path: '/guidances',
        category: 'guide',
        colors,
        specificType,
      }
    }

    // Tax Treaties
    if (slug.includes('-dtaa')) {
      return {
        typeLabel: 'Tax Treaty',
        icon: <PublicIcon fontSize='small' />,
        path: '/tax-treaties',
        category: 'treaty',
        colors: defaultColors.treaty,
      }
    }

    // Default fallback
    return {
      typeLabel: 'Document',
      icon: <DescriptionIcon fontSize='small' />,
      path: '/articles',
      category: 'article',
      colors: defaultColors.article,
    }
  }

  /**
   * Handle document click - open in new tab
   * @param {string} docSlug - Document slug
   * @param {object} docInfo - Document information
   */
  const handleDocumentClick = (docSlug, docInfo) => {
    const fullUrl = `${window.location.origin}${docSlug}`
    openDocumentInTab(fullUrl, docSlug, showSnackbar)
  }

  return (
    <Paper
      elevation={2}
      sx={{
        padding: 3,
        marginTop: 4,
        marginBottom: 2,
        borderRadius: '12px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef',
      }}
    >
      {/* Header */}
      <Typography
        variant='h6'
        sx={{
          fontWeight: 600,
          marginBottom: 2.5,
          color: '#232536',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <ArticleIcon sx={{ color: '#5948CA' }} />
        Related Documents
      </Typography>
      {/* Helper Text */}
      {/* <Typography
        variant='caption'
        sx={{
          display: 'block',
          marginBottom: 2,
          color: '#6c757d',
          fontStyle: 'italic',
        }}
      >
        Click on any document to view its content in a new tab
      </Typography> */}

      {/* Documents List - Each document in a row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {relatedDocs.map((docSlug, index) => {
          const docInfo = getDocumentInfo(docSlug)

          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                padding: 2,
                // flexWrap: 'wrap',
                // padding: 1.5,
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f8f9fa',
                  borderColor: docInfo.colors.main,
                  boxShadow: `0 2px 12px ${docInfo.colors.light}`,
                  transform: 'translateY(-2px)',
                },
              }}
              onClick={() => handleDocumentClick(docSlug, docInfo)}
              role='button'
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleDocumentClick(docSlug, docInfo)
                }
              }}
            >
              {/* Top row: Type chip and open icon */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                {/* Document Type Chip */}
                <Chip
                  icon={docInfo.icon}
                  label={docInfo.typeLabel}
                  size='small'
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    backgroundColor: docInfo.colors.light,
                    color: docInfo.colors.main,
                    border: `1px solid ${docInfo.colors.main}`,
                    '& .MuiChip-icon': {
                      color: docInfo.colors.main,
                    },
                  }}
                />
                {/* Open in new tab icon */}

                <OpenInNewIcon
                  sx={{
                    fontSize: '1rem',
                    color: '#6c757d',
                    transition: 'color 0.2s ease',
                    '&:hover': {
                      color: docInfo.colors.main,
                    },
                  }}
                />
              </Box>
              {/* Document Slug Chip */}
              <Typography
                variant='body1'
                sx={{
                  fontWeight: 700, // Bold for prominence
                  fontSize: '0.95rem', // Larger, readable size
                  color: '#232536', // Dark, easy to read
                  lineHeight: 1.4, // Good spacing
                  wordBreak: 'break-word',
                  letterSpacing: '-0.01em', // Tighter, more modern
                  fontFamily: 'system-ui, -apple-system, sans-serif', // Clean, native font
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: docInfo.colors.main,
                  },
                }}
              >
                {docSlug.split('/').pop()}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Paper>
  )
}

export default RelatedDocuments
